import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { COMPETENCY_LABELS } from '@/lib/competency'
import type { Competency, CreateStoryInput, CreateStoryResult } from '@/types/interview'
import { useState } from 'react'

const STAR_FIELDS = [
  {
    key: 'situation',
    label: 'Situation',
    placeholder: 'Set the scene — the class, club, job, or project and what was at stake',
    required: true,
  },
  { key: 'task', label: 'Task', placeholder: 'What was YOUR responsibility?', required: false },
  {
    key: 'action',
    label: 'Action',
    placeholder: 'What did you actually do, step by step?',
    required: false,
  },
  {
    key: 'result',
    label: 'Result',
    placeholder: 'What changed? Numbers are best',
    required: false,
  },
] as const

interface ManualStoryDialogProps {
  open: boolean
  onClose: () => void
  onCreate: (input: CreateStoryInput) => Promise<CreateStoryResult>
  onCapHit: (storyCount: number) => void
}

export function ManualStoryDialog({ open, onClose, onCreate, onCapHit }: ManualStoryDialogProps) {
  const [title, setTitle] = useState('')
  const [competency, setCompetency] = useState<Competency | ''>('')
  const [star, setStar] = useState<Record<string, string>>({})
  const [roleTag, setRoleTag] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit =
    title.trim().length > 0 && competency !== '' && (star.situation ?? '').trim().length > 0

  function reset() {
    setTitle('')
    setCompetency('')
    setStar({})
    setRoleTag('')
    setError(null)
  }

  async function handleSubmit() {
    if (!canSubmit || !competency) return
    setSubmitting(true)
    setError(null)

    const result = await onCreate({
      source: 'manual',
      title: title.trim(),
      competency,
      star: {
        situation: (star.situation ?? '').trim(),
        ...(star.task?.trim() && { task: star.task.trim() }),
        ...(star.action?.trim() && { action: star.action.trim() }),
        ...(star.result?.trim() && { result: star.result.trim() }),
      },
      ...(roleTag.trim() && { roleTag: roleTag.trim() }),
    })
    setSubmitting(false)

    if (result.ok) {
      reset()
      onClose()
    } else if (result.code === 'STORY_CAP_REACHED') {
      onCapHit(result.storyCount)
      onClose()
    } else {
      setError('We could not save your story. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="text-base">Add a story</DialogTitle>
          <DialogDescription className="text-xs">
            Any experience counts — group projects, part-time jobs, clubs, volunteering.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label
              htmlFor="story-title"
              className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
            >
              Title
            </label>
            <input
              id="story-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Rescued our stalled group project"
              className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Competency
              </span>
              <select
                value={competency}
                onChange={(e) => setCompetency(e.target.value as Competency)}
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="" disabled>
                  Pick one
                </option>
                {(Object.keys(COMPETENCY_LABELS) as Competency[]).map((key) => (
                  <option key={key} value={key}>
                    {COMPETENCY_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label
                htmlFor="story-role-tag"
                className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
              >
                Context (optional)
              </label>
              <input
                id="story-role-tag"
                value={roleTag}
                onChange={(e) => setRoleTag(e.target.value)}
                maxLength={80}
                placeholder="e.g. Club treasurer"
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {STAR_FIELDS.map(({ key, label, placeholder, required }) => (
            <div key={key} className="space-y-1">
              <label
                htmlFor={`story-${key}`}
                className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
              >
                {label}
                {required ? '' : ' (optional)'}
              </label>
              <textarea
                id={`story-${key}`}
                value={star[key] ?? ''}
                onChange={(e) => setStar((prev) => ({ ...prev, [key]: e.target.value }))}
                rows={2}
                maxLength={2000}
                placeholder={placeholder}
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
          ))}
        </div>

        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || submitting}
            className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Save story'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
